import { lazy, Suspense } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import HeroLogo from "./HeroLogo";
import { useLeadModal } from "./LeadModalContext";
import { useContent } from "../../context/ContentContext";

// HeroVisual contains a heavy live "console" mockup with rotating ticker animations.
// Code-splitting it shaves a sizeable chunk off the initial JS bundle and lets the
// lockup + headline render first — perceived speed boost on mobile.
const HeroVisual = lazy(() => import("./HeroVisual"));

export default function Hero() {
  const { open: openLead } = useLeadModal();
  const { t } = useContent();
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-40 sm:pt-52 pb-20 sm:pb-32 ocean-glow"
    >
      <div className="absolute inset-0 subtle-grid pointer-events-none" />
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 h-[600px] w-[1100px] rounded-full bg-[#0055FF]/22 blur-[140px]" />
      <div className="pointer-events-none absolute top-[18%] right-[8%] h-[300px] w-[300px] rounded-full bg-[#00E5FF]/10 blur-[110px]" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
        <div className="flex justify-center mb-7 sm:mb-9">
          <HeroLogo size={56} gap={16} />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="flex justify-center"
        >
          <div
            data-testid="hero-eyebrow"
            className="inline-flex items-center gap-2 rounded-full glass px-3.5 py-1.5 text-[11.5px] uppercase tracking-[0.18em] text-zinc-300"
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#00E5FF] opacity-75 animate-ping" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#00E5FF]" />
            </span>
            {t("homepage.hero.eyebrow", "Payment infrastructure · v3")}
          </div>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.05 }}
          data-testid="hero-headline"
          className="font-display mt-8 text-center text-[46px] leading-[1.04] sm:text-7xl lg:text-[92px] lg:leading-[1.0] font-semibold tracking-tighter chrome-text"
        >
          {t("homepage.hero.headline_part1", "Payments, reimagined")}
          <br className="hidden sm:block" />
          <span className="ocean-text"> {t("homepage.hero.headline_part2", "for a new era.")}</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15 }}
          data-testid="hero-subheadline"
          className="mt-8 text-center text-[16px] sm:text-[19px] leading-relaxed text-zinc-400 max-w-xl mx-auto"
        >
          {t("homepage.hero.subheadline", "One infrastructure to simplify global payments.")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.25 }}
          className="mt-10 flex items-center justify-center gap-3 flex-wrap"
        >
          <Link
            to="/signup"
            data-testid="hero-get-started-btn"
            className="group inline-flex items-center justify-center gap-1.5 rounded-full bg-white text-black hover:bg-zinc-200 transition-all px-6 py-3.5 text-[14px] font-medium"
          >
            {t("homepage.hero.cta_label", "Get Started")}
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </Link>
          <button
            type="button"
            onClick={() => openLead("contact")}
            data-testid="hero-talk-to-sales-btn"
            className="group inline-flex items-center justify-center gap-1.5 rounded-full border border-white/[0.12] bg-white/[0.02] text-white hover:bg-white/[0.06] hover:border-white/[0.22] transition-colors px-6 py-3.5 text-[14px] font-medium"
          >
            Talk to sales
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4 }}
          className="mt-20 sm:mt-28"
        >
          <Suspense
            fallback={
              <div className="h-[420px] sm:h-[520px] rounded-3xl border border-white/[0.06] bg-white/[0.015]" />
            }
          >
            <HeroVisual />
          </Suspense>
        </motion.div>
      </div>
    </section>
  );
}
